import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DataContext } from "../../context/DataContext";
import { callPostApi } from "../../function/ApiCallFunction";
import { clubPost } from "../club/clubApi";
import { consoleColors, primaryButtonSx } from "../admin/consoleTheme";
import { canAdd, canEdit, hasMenu, isOwner, STORE_MENU_CATALOG, CLUB_MENU_CATALOG } from "../../include/staffAccess";

function paths(source, plus) {
  if (source === "club") return { list: "/club/users", add: "/club/users/new", edit: (id) => `/club/users/${id}/edit` };
  if (plus) return { list: "/dashboardPlus/users", add: "/dashboardPlus/users/new", edit: (id) => `/dashboardPlus/users/${id}/edit` };
  return { list: "/users", add: "/users/new", edit: (id) => `/users/${id}/edit` };
}

function postFor(source, storeId, clubId, mod, data = {}) {
  if (source === "club") return clubPost(mod, { club_id: clubId, ...data });
  return callPostApi("store-draw", { mod, data_arr: { store_id: storeId, ...data } });
}

function sessionOf(source, loginData, clubData) {
  return source === "club" ? clubData : loginData;
}

export default function Users({ source = "store" }) {
  const navigate = useNavigate();
  const { loginData, clubData } = useContext(DataContext);
  const location = useLocation();
  const plus = location.pathname.startsWith("/dashboardPlus");
  const storeId = loginData?.store_info?.store_id;
  const clubId = clubData?.club_info?.club_id;
  const session = sessionOf(source, loginData, clubData);
  const route = paths(source, plus);
  const [rows, setRows] = useState([]);
  const ready = source === "club" ? Boolean(clubId) : Boolean(storeId);
  const allowAdd = hasMenu(session, "users") && (isOwner(session) || canAdd(session));

  const load = useCallback(async () => {
    if (!ready) return;
    const result = await postFor(source, storeId, clubId, "STAFF_LIST");
    if (result.status === "200") setRows(result.data?.users || []);
  }, [ready, source, storeId, clubId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (row) => {
    const result = await postFor(source, storeId, clubId, "STAFF_STATUS", { user_id: row.user_id, status: row.status === "1" ? "0" : "1" });
    if (result.status === "200") {
      toast.success(result.data?.message || "Updated");
      load();
    } else toast.info(result.status_message || result.data?.error || "Could not update");
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, gap: 1, flexWrap: "wrap" }}>
        <Typography sx={{ color: consoleColors.muted, fontSize: 14, maxWidth: 640 }}>
          Add users with an email. Store users sign in with a 6-digit email code. Each user only sees the menus you allow.
        </Typography>
        {allowAdd && (
          <Button variant="contained" sx={{ ...primaryButtonSx, whiteSpace: "nowrap" }} onClick={() => navigate(route.add)}>
            <Add sx={{ mr: 0.5 }} /> Add user
          </Button>
        )}
      </Box>

      {rows.length === 0 && (
        <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, p: 4, textAlign: "center", color: consoleColors.muted }}>
          No users yet.
        </Box>
      )}

      <Box sx={{ display: "grid", gap: 1.5 }}>
        {rows.map((row) => (
          <Box key={row.user_id} sx={{ background: row.status === "1" ? "#fff" : "#f6eaea", border: `1px solid ${row.status === "1" ? consoleColors.line : "#e7cfcf"}`, borderRadius: 2.5, p: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontSize: 17, fontWeight: 700 }}>{row.name}</Typography>
              <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: 0.4 }}>
                {row.email || row.username} · {row.menus?.length || 0} menus · {row.can_add === "1" ? "Add" : "No add"} · {row.can_edit === "1" ? "Edit" : "No edit"} · {row.status === "1" ? "Allowed" : "Blocked"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {(isOwner(session) || canEdit(session)) && (
                <>
                  <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(route.edit(row.user_id))}>Edit</Button>
                  <Button size="small" sx={{ textTransform: "none" }} onClick={() => toggle(row)}>{row.status === "1" ? "Block" : "Allow"}</Button>
                </>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function UserForm({ source = "store" }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { loginData, clubData } = useContext(DataContext);
  const location = useLocation();
  const plus = location.pathname.startsWith("/dashboardPlus");
  const storeId = loginData?.store_info?.store_id;
  const clubId = clubData?.club_info?.club_id;
  const route = paths(source, plus);
  const [menus, setMenus] = useState(source === "club" ? CLUB_MENU_CATALOG : STORE_MENU_CATALOG);
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "", menus: [], can_add: "1", can_edit: "1", status: "1" });
  const [saving, setSaving] = useState(false);
  const ready = source === "club" ? Boolean(clubId) : Boolean(storeId);

  useEffect(() => {
    if (!ready) return;
    const catalog = source === "club" ? CLUB_MENU_CATALOG : STORE_MENU_CATALOG;
    postFor(source, storeId, clubId, "STAFF_LIST").then((result) => {
      if (result.status !== "200") {
        setMenus(catalog);
        return;
      }
      setMenus(result.data?.menus?.length ? result.data.menus : catalog);
      const current = (result.data?.users || []).find((row) => row.user_id === userId);
      if (current) {
        setForm({
          name: current.name || "",
          username: current.username || "",
          email: current.email || "",
          password: "",
          menus: current.menus || [],
          can_add: current.can_add || "1",
          can_edit: current.can_edit || "1",
          status: current.status || "1",
        });
      }
    });
  }, [ready, source, storeId, clubId, userId]);

  const grouped = useMemo(() => {
    const map = {};
    menus.forEach((item) => {
      map[item.group] = map[item.group] || [];
      map[item.group].push(item);
    });
    return Object.entries(map);
  }, [menus]);

  const toggleMenu = (id) => {
    setForm((prev) => ({
      ...prev,
      menus: prev.menus.includes(id) ? prev.menus.filter((item) => item !== id) : [...prev.menus, id],
    }));
  };

  const save = async () => {
    if (source === "store" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(form.email || "").trim())) {
      toast.info("A valid email is required");
      return;
    }
    setSaving(true);
    try {
      const result = await postFor(source, storeId, clubId, "STAFF_SAVE", { ...form, user_id: userId || "" });
      if (result.status === "200") {
        toast.success(result.data?.message || "User saved");
        navigate(route.list);
      } else toast.info(result.status_message || result.data?.error || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 760 }}>
      <Button sx={{ textTransform: "none", mb: 2 }} onClick={() => navigate(route.list)}>Back to users</Button>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 }, display: "grid", gap: 1.5 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 750 }}>{userId ? "Edit user" : "Add user"}</Typography>
        <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>
          {source === "club"
            ? "This login id and password sign in at the club page. Only checked menus appear after login."
            : "This email signs in at the store page with a 6-digit code. Only checked menus appear after login."}
        </Typography>
        <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <TextField label="Login id" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} helperText="3-32 letters, numbers, dot, dash, or underscore" />
        {source === "store" ? (
          <TextField required label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} helperText="Required. This user signs in with a 6-digit code sent here." />
        ) : (
          <TextField label={userId ? "New password" : "Password"} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} helperText={userId ? "Leave blank to keep the current password" : "At least 4 characters"} />
        )}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControlLabel control={<Checkbox checked={form.can_add === "1"} onChange={(e) => setForm({ ...form, can_add: e.target.checked ? "1" : "0" })} />} label="Allow add" />
          <FormControlLabel control={<Checkbox checked={form.can_edit === "1"} onChange={(e) => setForm({ ...form, can_edit: e.target.checked ? "1" : "0" })} />} label="Allow edit" />
          <TextField select size="small" label="Allow login" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} sx={{ minWidth: 160 }}>
            <MenuItem value="1">Allowed</MenuItem>
            <MenuItem value="0">Blocked</MenuItem>
          </TextField>
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 750 }}>Menu permission</Typography>
        {grouped.map(([group, items]) => (
          <Box key={group}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: consoleColors.muted, mb: 0.4 }}>{group.toUpperCase()}</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 0.2 }}>
              {items.map((item) => (
                <FormControlLabel
                  key={item.id}
                  control={<Checkbox size="small" checked={form.menus.includes(item.id)} onChange={() => toggleMenu(item.id)} />}
                  label={item.label}
                />
              ))}
            </Box>
          </Box>
        ))}
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button sx={{ textTransform: "none" }} onClick={() => navigate(route.list)}>Cancel</Button>
          <Button variant="contained" sx={primaryButtonSx} disabled={saving} onClick={save}>{saving ? "Saving..." : "Save user"}</Button>
        </Box>
      </Box>
    </Box>
  );
}
